import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';

import { checkIsContainerType } from 'utils/documentFieldTypes';

import {
  buildNestedExtractionFields,
  checkIsLowConfidence,
} from './DocumentExtractionFieldsTab/DocumentExtractionFieldsTab.utils';
import {
  EXTRACTION_PANEL_STATUS,
  EXTRACTION_REQUEST_STATUS_TYPE,
  EXTRACTION_SESSION_MODE_TYPE,
  EXTRACTION_SESSION_STATUS_TYPE,
} from './DocumentInspector.constants';

dayjs.extend(relativeTime);
dayjs.extend(utc);

const flattenLeaves = fields =>
  _.sortBy(fields ?? [], 'orderNumber').reduce((acc, field) => {
    const type = field?.documentFieldType?.typeDefinition?.type;
    const children = field?.Children ?? [];

    if (checkIsContainerType(type)) {
      acc.push(...flattenLeaves(children));

      return acc;
    }

    acc.push({ id: field.id, name: field.name, orderNumber: field.orderNumber });

    return acc;
  }, []);

/**
 * Resolves the document type's field DEFINITIONS to an ordered flat list of leaf
 * fields (containers/tables contribute their children, not themselves). This is the
 * denominator of the "N of M fields extracted" badge and the source for rendering
 * fields that have NO extraction result — definitions must render even when
 * extraction found nothing (previously they were silently dropped).
 *
 * @param {{
 *   extractionFields: Array<Object>|null|undefined,  // nested defs embedded on document.Type
 *   inspectorFlatFields: Array<Object>|null|undefined, // flat defs loaded by the inspector fallback
 * }} params
 * @returns {Array<{ id: string, name: string, orderNumber: number }>}
 */
export const getExtractionFieldLeaves = ({ extractionFields, inspectorFlatFields }) => {
  const resolvedFields = !_.isEmpty(extractionFields)
    ? extractionFields
    : buildNestedExtractionFields(inspectorFlatFields ?? []);

  return flattenLeaves(resolvedFields);
};

/**
 * Validates the extraction results payload shape before it is joined onto the
 * definitions. A backend contract drift (missing documentExtractionFieldId) must
 * surface the error banner instead of silently rendering an empty tree.
 *
 * @param {*} results
 * @returns {boolean} true when results is an array of objects each carrying documentExtractionFieldId
 */
export const checkExtractionResultsShape = results => {
  if (!Array.isArray(results)) {
    return false;
  }

  return results.every(result => _.isObject(result) && !_.isNil(result.documentExtractionFieldId));
};

/**
 * Distinct count of definition leaves that extraction found at least one value for.
 * Results not matching any known definition leaf are ignored (guards against a
 * stale result set from a previously inspected document of another type).
 *
 * @param {{ leaves: Array<{ id: string }>, extractionFieldsResults: Array<Object>|null|undefined }} params
 * @returns {number}
 */
export const countExtractedFieldResults = ({ leaves, extractionFieldsResults }) => {
  const leafIds = new Set(leaves.map(({ id }) => id));

  const extractedIds = (extractionFieldsResults ?? [])
    .map(({ documentExtractionFieldId }) => documentExtractionFieldId)
    .filter(id => leafIds.has(id));

  return new Set(extractedIds).size;
};

/**
 * The definition leaves extraction found NO value for — rendered as the
 * "Not found on document (N)" accordion (or as empty em-dash cards when
 * nothing was extracted at all) instead of being silently dropped.
 *
 * @param {{ leaves: Array<{ id: string }>, extractionFieldsResults: Array<Object>|null|undefined }} params
 * @returns {Array<{ id: string, name: string, orderNumber: number }>}
 */
export const getMissingExtractionFieldLeaves = ({ leaves, extractionFieldsResults }) => {
  const extractedIds = new Set(
    (extractionFieldsResults ?? []).map(
      ({ documentExtractionFieldId }) => documentExtractionFieldId,
    ),
  );

  return leaves.filter(({ id }) => !extractedIds.has(id));
};

/**
 * Count of results whose confidence fell below their per-field threshold —
 * surfaced in the header meta line for the needs-review state.
 *
 * @param {Array<Object>|null|undefined} extractionFieldsResults
 * @returns {number}
 */
export const countBelowThresholdResults = extractionFieldsResults =>
  (extractionFieldsResults ?? []).filter(({ confidence, threshold }) =>
    checkIsLowConfidence(confidence, threshold),
  ).length;

const checkHasCompletedGenAiSession = sessions =>
  (sessions ?? []).some(
    session =>
      session?.status?.type === EXTRACTION_SESSION_STATUS_TYPE.COMPLETED &&
      session?.mode?.type === EXTRACTION_SESSION_MODE_TYPE.GEN_AI,
  );

/**
 * Derives the UI panel status from the document's embedded extraction request.
 *
 * ReadyForExtractor splits on session history: a completed GenAI session means the
 * AI already ran and the result awaits human review; no completed GenAI session
 * means nothing ran yet and the document waits in the manual entry queue.
 *
 * @param {{ status: { type: string }, mode: string, sessions: Array<Object> }|null|undefined} extractionRequest
 * @returns {string|null} one of EXTRACTION_PANEL_STATUS, or null when the request is absent/unknown
 */
export const getExtractionPanelStatus = extractionRequest => {
  const statusType = extractionRequest?.status?.type;

  if (_.isNil(statusType)) {
    return null;
  }

  if (statusType === EXTRACTION_REQUEST_STATUS_TYPE.READY_FOR_EXTRACTOR) {
    return checkHasCompletedGenAiSession(extractionRequest?.sessions)
      ? EXTRACTION_PANEL_STATUS.NEEDS_REVIEW
      : EXTRACTION_PANEL_STATUS.AWAITING_MANUAL;
  }

  const panelStatusByRequestStatus = {
    [EXTRACTION_REQUEST_STATUS_TYPE.PROCESSED]: EXTRACTION_PANEL_STATUS.PROCESSED,
    [EXTRACTION_REQUEST_STATUS_TYPE.AI_PROCESSING]: EXTRACTION_PANEL_STATUS.PROCESSING,
    [EXTRACTION_REQUEST_STATUS_TYPE.EXTRACTOR_PROCESSING]: EXTRACTION_PANEL_STATUS.PROCESSING,
    [EXTRACTION_REQUEST_STATUS_TYPE.REJECTED]: EXTRACTION_PANEL_STATUS.FAILED,
    [EXTRACTION_REQUEST_STATUS_TYPE.NOT_STARTED]: EXTRACTION_PANEL_STATUS.QUEUED,
    [EXTRACTION_REQUEST_STATUS_TYPE.SKIPPED_FOR_MANUAL]: EXTRACTION_PANEL_STATUS.AWAITING_MANUAL,
  };

  return panelStatusByRequestStatus[statusType] ?? null;
};

// Session timestamps come back without a zone suffix but ARE UTC — parse them as
// UTC (dayjs.utc) or a relative "2m ago" renders as a future "in 3 hours".
const getLatestSessionDate = sessions => {
  const dates = (sessions ?? [])
    .map(session => session?.status?.completedDate ?? session?.startDate)
    .filter(date => !_.isNil(date) && dayjs.utc(date).isValid());

  return _.maxBy(dates, date => dayjs.utc(date).valueOf()) ?? null;
};

/**
 * Builds the small grey meta line next to the count badge ("AI extraction · 2m ago",
 * "started 8s ago", "failed 1m ago", "… · 2 below field threshold"). Returns null
 * when session data provides no usable timestamp.
 *
 * @param {{ extractionRequest: Object|null|undefined, panelStatus: string|null, belowThresholdCount: number }} params
 * @returns {string|null}
 */
export const getExtractionStatusMeta = ({
  extractionRequest,
  panelStatus,
  belowThresholdCount = 0,
}) => {
  const latestDate = getLatestSessionDate(extractionRequest?.sessions);
  const relative = _.isNil(latestDate) ? null : dayjs.utc(latestDate).fromNow();

  if (_.isNil(relative)) {
    return null;
  }

  if (panelStatus === EXTRACTION_PANEL_STATUS.PROCESSING) {
    return `started ${relative}`;
  }

  if (panelStatus === EXTRACTION_PANEL_STATUS.FAILED) {
    return `failed ${relative}`;
  }

  if (panelStatus === EXTRACTION_PANEL_STATUS.PROCESSED) {
    return `AI extraction · ${relative}`;
  }

  if (panelStatus === EXTRACTION_PANEL_STATUS.NEEDS_REVIEW) {
    const thresholdSuffix =
      belowThresholdCount > 0 ? ` · ${belowThresholdCount} below field threshold` : '';

    return `AI extraction · ${relative}${thresholdSuffix}`;
  }

  return null;
};

/**
 * Builds the "N of M fields extracted" header badge parts. For the manual-entry /
 * queued states (nothing extracted by design) it degrades to a plain "M fields".
 * N renders as an em-dash while extraction is running or results have not loaded
 * for this document yet.
 *
 * @param {{ panelStatus: string|null, extractedCount: number, totalCount: number, isResultsLoaded: boolean }} params
 * @returns {{ primary: string, rest: string }|null} null when the definitions have not loaded
 */
export const getExtractionCountText = ({
  panelStatus,
  extractedCount,
  totalCount,
  isResultsLoaded,
}) => {
  if (!totalCount) {
    return null;
  }

  const isPlainCountStatus =
    panelStatus === EXTRACTION_PANEL_STATUS.AWAITING_MANUAL ||
    panelStatus === EXTRACTION_PANEL_STATUS.QUEUED;

  if (isPlainCountStatus) {
    return { primary: String(totalCount), rest: totalCount === 1 ? ' field' : ' fields' };
  }

  const isCountKnown = isResultsLoaded && panelStatus !== EXTRACTION_PANEL_STATUS.PROCESSING;
  const primary = isCountKnown ? String(extractedCount) : '—';

  return { primary, rest: ` of ${totalCount} fields extracted` };
};
